import { defineStore } from 'pinia';
import { ref, computed, watchEffect } from 'vue';
import { useAuthStore } from './authStore';
import { useLogger } from '../composables/useLogger';
import { useCentralConfigService } from '../composables/useCentralConfigService';
import { ANNIE_ELK_USER_ID } from '~/src/constants';
// Define the configuration interface in the store
export interface ConfigResponse {
    [key: string]: any;
    isVoiceV1Available: boolean;
    isVoiceV1AvailableInOnboarding: boolean;
    isHideExtendedMenu: boolean;
}

// Default configuration used when user ID is null or when server fetch fails
const DEFAULT_CONFIG: ConfigResponse = {
    isVoiceV1Available: false,
    isVoiceV1AvailableInOnboarding: false,
    isHideExtendedMenu: false
};

export const useCentralConfigStore = defineStore('centralConfig', () => {
    // Configuration variables
    const config = ref<ConfigResponse | null>(null);
    const isInitialized = ref(false);
    const logger = useLogger();
    const { fetchConfig } = useCentralConfigService();
    const userId = ref<string | null>(null);
    const helperId = ref<string | null>(null);
    const helperEmail = ref<string | null>(null);
    const userCreationDate = ref<string | null>(null);
    // Track initialization promise
    let initPromise: Promise<void> | null = null;

    // Computed properties to expose specific config values
    const isVoiceV1Available = computed(() => {
        let retval = config.value?.isVoiceV1Available ?? false;
        return retval;
    }
    );

    const isVoiceV1AvailableInOnboarding = computed(() => {
        return config.value?.isVoiceV1AvailableInOnboarding ?? false;
    });

    const isUseFullFeaturedChat = computed(() => {
        let retval = false;
        if (helperEmail.value?.endsWith("morten@copyrightagent.com")) {
            retval = true;
        }
        return retval;
    });

    const isHideExtendedMenu = computed(() => {
        return config.value?.isHideExtendedMenu ?? false;
    });

    // Load configuration from server
    async function loadConfig() {
        try {
            logger.debug('CentralConfig: Loading configuration for these values', { 
                userId: userId.value, 
                helperId: helperId.value, 
                helperEmail: helperEmail.value,
                creationDate: userCreationDate.value 
            });

            const response = await fetchConfig({
                userId: userId.value,
                creationDate: userCreationDate.value,
                helperId: helperId.value,
                helperEmail: helperEmail.value
            });

            // Store the complete configuration object
            config.value = response as ConfigResponse;

            logger.debug('CentralConfig: Configuration loaded successfully', { config: config.value });

            isInitialized.value = true;
        } catch (error) {
            // fall back to default setting, log error, but continue
            logger.logErrorCaught('CentralConfig: Failed to load configuration, using default settings', 'centralConfigStore.ts', error);
            config.value = { ...DEFAULT_CONFIG };
            isInitialized.value = true;
        }
    }

    // Function to ensure the store is initialized - exposed to the outside world
    function ensureInitialized(): Promise<void> {
        // logger.debug('CentralConfig: Ensuring initialization', { isInitialized: isInitialized.value, initPromise: initPromise });
        const authStore = useAuthStore();

        if (!isInitialized.value) {
            if (!initPromise) {
                logger.debug('CentralConfig: Initializing');
                initPromise = loadConfig().finally(() => {
                    initPromise = null;
                });
            } else {
                logger.debug('CentralConfig: Initialization already in progress, reusing promise');
            }
        }
        else {
            // logger.debug('CentralConfig: Already initialized, doing nothing');
        }
        return initPromise;
    }

    // Watch for relevant auth store changes and reload config
    const authStore = useAuthStore();
    watchEffect(() => {
        // Get values from auth store to establish reactive dependencies
        const newUserId = authStore.userId;
        const newHelperId = authStore.helperId;
        const newHelperPerson = authStore.helperPerson;
        const newUser = authStore.user;

        // Update our local refs
        userId.value = newUserId;
        helperId.value = newHelperId;
        helperEmail.value = newHelperPerson?.email;
        userCreationDate.value = newUser?.created_at || null;

        logger.debug('CentralConfig: Auth store changes detected, updating configuration', {
            userId: newUserId,
            helperId: newHelperId,
            helperEmail: newHelperPerson?.email,
            userCreationDate: userCreationDate.value
        });

        loadConfig();
    });

    // Initialize on store creation regardless of user login status
    logger.debug('CentralConfig: Initializing store');
    ensureInitialized();

    return {
        config,
        isVoiceV1Available,
        isVoiceV1AvailableInOnboarding,
        isUseFullFeaturedChat,
        isHideExtendedMenu,
        isInitialized,
        ensureInitialized,
    };
}); 